// parameters.js — пользовательские параметры и вычисление выражений.
//
// Как в Fusion 360: параметр = { name, expression, value, unit, comment }.
// Выражение может ссылаться на другие параметры по имени и использовать
// арифметику + функции (sin, cos, sqrt, ...). Значения углов в выражениях
// тригонометрии — в ГРАДУСАХ (как в Fusion: sin(30) = 0.5).
//
// Безопасность: НЕ используем eval/Function. Свой парсер (шант-ярд →
// вычисление стека), только разрешённые токены. Это важно, т.к. документ
// (с выражениями) может прийти из облака от другого пользователя.

// Разрешённые функции (аргумент углов — в градусах для тригонометрии).
const DEG = Math.PI / 180;
const FUNCS = {
    sin:  (x) => Math.sin(x * DEG),
    cos:  (x) => Math.cos(x * DEG),
    tan:  (x) => Math.tan(x * DEG),
    asin: (x) => Math.asin(x) / DEG,
    acos: (x) => Math.acos(x) / DEG,
    atan: (x) => Math.atan(x) / DEG,
    sqrt: Math.sqrt,
    abs:  Math.abs,
    floor: Math.floor,
    ceil: Math.ceil,
    round: Math.round,
    min:  Math.min,
    max:  Math.max,
    pow:  Math.pow,
    ln:   Math.log,
    log:  Math.log10,
    // Добор до полного набора Fusion 360: гиперболические, экспонента,
    // знак и случайное число. Без них выражения, скопированные из Fusion,
    // молча не считались.
    sinh: Math.sinh,
    cosh: Math.cosh,
    tanh: Math.tanh,
    asinh: (x) => Math.asinh(x) / DEG,
    acosh: (x) => Math.acosh(x) / DEG,
    atanh: (x) => Math.atanh(x) / DEG,
    exp:  Math.exp,
    sign: Math.sign,
    random: Math.random,
};
const CONSTS = { pi: Math.PI, PI: Math.PI, e: Math.E };


// --- Токенизатор -----------------------------------------------------------
// Числа, идентификаторы, операторы + - * / ^ ( ) , и пробелы.
function tokenize(expr) {
    const tokens = [];
    let i = 0;
    const s = String(expr);
    while (i < s.length) {
        const c = s[i];
        if (c === ' ' || c === '\t') { i++; continue; }
        if (/[0-9.]/.test(c)) {
            let num = '';
            while (i < s.length && /[0-9.]/.test(s[i])) { num += s[i++]; }
            tokens.push({ t: 'num', v: parseFloat(num) });
            continue;
        }
        if (/[a-zA-Zа-яА-Я_]/.test(c)) {
            let id = '';
            while (i < s.length && /[a-zA-Zа-яА-Я0-9_]/.test(s[i])) { id += s[i++]; }
            tokens.push({ t: 'id', v: id });
            continue;
        }
        /*
         * Разделитель аргументов.
         *
         * 🔴 В Fusion это ТОЧКА С ЗАПЯТОЙ: min(5; 10). Так сделано потому,
         * что во многих локалях запятая — десятичный разделитель. Принимаем
         * оба варианта, чтобы формулы из Fusion работали как есть.
         */
        if (c === ';') { tokens.push({ t: 'op', v: ',' }); i++; continue; }
        if ('+-*/^(),'.includes(c)) {
            tokens.push({ t: 'op', v: c });
            i++;
            continue;
        }
        throw new Error(`Недопустимый символ: «${c}»`);
    }
    return tokens;
}


// --- Парсер (рекурсивный спуск) → значение ---------------------------------
// grammar:
//   expr   = term (('+'|'-') term)*
//   term   = power (('*'|'/') power)*
//   power  = unary ('^' power)?
//   unary  = ('-'|'+')? atom
//   atom   = num | const | ident | func '(' args ')' | '(' expr ')'
//
// resolve(name) — функция, возвращающая значение параметра по имени (число)
// либо undefined, если такого нет.
function evaluate(expr, resolve) {
    const tokens = tokenize(expr);
    let pos = 0;
    const peek = () => tokens[pos];
    const next = () => tokens[pos++];

    function parseExpr() {
        let v = parseTerm();
        while (peek() && peek().t === 'op' && (peek().v === '+' || peek().v === '-')) {
            const op = next().v;
            const r = parseTerm();
            v = op === '+' ? v + r : v - r;
        }
        return v;
    }
    function parseTerm() {
        let v = parsePower();
        while (peek() && peek().t === 'op' && (peek().v === '*' || peek().v === '/')) {
            const op = next().v;
            const r = parsePower();
            v = op === '*' ? v * r : v / r;
        }
        return v;
    }
    function parsePower() {
        const v = parseUnary();
        if (peek() && peek().t === 'op' && peek().v === '^') {
            next();
            return Math.pow(v, parsePower());
        }
        return v;
    }
    function parseUnary() {
        if (peek() && peek().t === 'op' && (peek().v === '-' || peek().v === '+')) {
            const op = next().v;
            const v = parseUnary();
            return op === '-' ? -v : v;
        }
        return parseAtom();
    }
    function parseAtom() {
        const tok = peek();
        if (!tok) throw new Error('Неожиданный конец выражения');
        if (tok.t === 'num') { next(); return tok.v; }
        if (tok.t === 'op' && tok.v === '(') {
            next();
            const v = parseExpr();
            if (!peek() || peek().v !== ')') throw new Error('Ожидалась «)»');
            next();
            return v;
        }
        if (tok.t === 'id') {
            next();
            // функция?
            if (peek() && peek().t === 'op' && peek().v === '(') {
                next();
                const args = [];
                if (peek() && peek().v !== ')') {
                    args.push(parseExpr());
                    while (peek() && peek().v === ',') { next(); args.push(parseExpr()); }
                }
                if (!peek() || peek().v !== ')') throw new Error('Ожидалась «)»');
                next();
                const fn = FUNCS[tok.v];
                if (!fn) throw new Error(`Неизвестная функция: ${tok.v}`);
                return fn(...args);
            }
            // константа?
            if (tok.v in CONSTS) return CONSTS[tok.v];
            // ссылка на параметр?
            const rv = resolve ? resolve(tok.v) : undefined;
            if (typeof rv === 'number' && Number.isFinite(rv)) return rv;
            throw new Error(`Неизвестный параметр: ${tok.v}`);
        }
        throw new Error(`Неожиданный токен: ${tok.v}`);
    }

    const result = parseExpr();
    if (pos < tokens.length) throw new Error('Лишние символы в выражении');
    if (!Number.isFinite(result)) throw new Error('Результат не число');
    return result;
}


/**
 * Вычислить все параметры документа с учётом взаимных ссылок.
 * params — [{ id, name, expression, unit, comment }].
 * Возвращает { values: Map<name, number>, errors: Map<id, string> }.
 * Ссылки резолвятся итеративно (до 20 проходов) — ловит циклы.
 */
export function computeParameters(params) {
    const values = new Map();
    const errors = new Map();
    const byName = new Map(params.map((p) => [p.name, p]));

    // Итеративный резолв: несколько проходов, пока значения «устаканиваются».
    for (let pass = 0; pass < 20; pass++) {
        let changed = false;
        for (const p of params) {
            if (!p.name) continue;
            try {
                const v = evaluate(p.expression, (name) => values.get(name));
                if (values.get(p.name) !== v) { values.set(p.name, v); changed = true; }
                errors.delete(p.id);
            } catch (e) {
                // На последнем проходе фиксируем ошибку (мог не резолвиться из-за
                // ещё не вычисленной ссылки — даём шанс следующим проходам).
                if (pass === 19) errors.set(p.id, e.message);
            }
        }
        if (!changed) break;
    }
    // Финальная проверка: у кого нет значения — ошибка (цикл/битая ссылка).
    for (const p of params) {
        if (p.name && !values.has(p.name) && !errors.has(p.id)) {
            errors.set(p.id, 'Не удалось вычислить (цикл или битая ссылка)');
        }
    }
    return { values, errors };
}


/**
 * Разрешить число ИЛИ выражение-параметр в конкретное значение.
 * Используется размерами эскиза / полями фич, которые могут быть привязаны
 * к параметру (строка-выражение) или быть простым числом.
 *
 *   resolveValue(50, values)            → 50
 *   resolveValue('Width', values)       → значение параметра Width
 *   resolveValue('Width/2 + 3', values) → вычисленное
 */
export function resolveValue(exprOrNumber, values) {
    if (typeof exprOrNumber === 'number') return exprOrNumber;
    if (exprOrNumber == null || exprOrNumber === '') return NaN;
    if (typeof exprOrNumber === 'string' && /^-?[0-9.]+$/.test(exprOrNumber.trim())) {
        return parseFloat(exprOrNumber);
    }
    try {
        return evaluate(exprOrNumber, (name) => values.get(name));
    } catch (_) {
        return NaN;
    }
}


// Экспортируем evaluate для точечного использования/тестов.
export { evaluate };
